import {useIntlEx} from "./IntlUtils";
import {TagBox, TagLabel} from "./TagBox";
import React, {useEffect, useMemo, useReducer, useRef, useState} from "react";
import {openDialog} from "./DialogUtils";
import {useSolv} from "./SolvProvider";
import {Widget, WidgetMetric} from "./Widget";
import {mkHref} from "./AccountUtils";
import {useQuery} from "react-query";
import {PayPalButton} from "react-paypal-button-v2";
import useStateRef from "react-usestateref";
import {ActionButton, FormGroup, FormGroups, Icon, NumericInput} from "./FormComps";
import {InfoBlock, InfoText} from "./ValidationUtils";
import {useReactToPrint} from "react-to-print";
import CreditTransactionBill from "../CreditTransactionBill";
import {pathReducer} from "./ReactUtils";
import getLogger from "./Logging.js"
import "../App.css"
import styles from "./CreditTransactionUtils.module.css"
import {StripeControls} from "./StripeComps";

const log = getLogger("CreditTransactions")

export function CreditsExpiring(credits) {
  let expiring = false
  if (credits && credits.amount > 0 && credits.expiresOn) {
    switch (credits.status) {
      case "EXPIRING_3":
        expiring = true
        break
      case "EXPIRING_2":
        expiring = true
        break
      case "EXPIRING_1":
        expiring = true
        break
      default:
        expiring = false
    }
  }
  return expiring
}

export function CreditsExpiringText(props) {
  const {intl} = useIntlEx()
  let {credits} = props
  let cls = ""
  let el = ""
  let expiring = CreditsExpiring(credits)
  if (expiring) {
    switch (credits.status) {
      case "EXPIRING_3":
        cls = "text-danger"
        break
      case "EXPIRING_2":
        cls = "text-warning"
        break
      case "EXPIRING_1":
        cls = "text-warning"
        break
      default:
        cls = ""
    }
    el = <>{intl.msg("credits_expires_days", {expireDays: intl.fmDaysFrom(credits.expiresOn)})}</>
    return (
      <span className={cls} title={intl.dateTime(credits.expiresOn)}>
        {el}
      </span>
    )
  } else if (props.alt) {
    return <span>
      {props.alt}
    </span>
  } else {
    return <></>
  }
}

export function CreditsExtendButton(props) {
  const {intl} = useIntlEx()
  const tenant = props.tenant
  const mode = props.mode || "BUTTON"
  return (
    tenant && CreditsExpiring(tenant.credits) && tenant.accessingAs("ADMIN/*/*")) ? (
    // tenant && (tenant.accessingAs("ADMIN/*/SYSTEM") || (isCreditsExpiring(tenant.credits) && tenant.accessingAs("ADMIN/*/*"))) ? (
    "BUTTON" === mode ? (
      <button className={"btn btn-secondary " + (props.className || "")} href="#"
              title={intl.msg("credit_transaction_extend_expiry_title")}
              onClick={() => openDialog("dlg_credits_extend_expiry")}>
        <i className="fas fa-history mr-1"></i>
        {intl.msg("credit_transaction_extend_expiry")}
      </button>
    ) : (
      <a className={"text-link " + (props.className || "")} href="#"
         onClick={() => openDialog("dlg_credits_extend_expiry")}>
        {intl.msg("credit_transaction_extend_expiry_title")}
      </a>
    )
  ) : (
    <></>
  )
}

export function CreditsWidget(props) {
  const {session} = useSolv()
  const {intl} = useIntlEx()
  const tenant = props.tenant
  return (
    tenant && tenant.credits &&
      <Widget title={intl.msg("credits")}
              subtitle={intl.msg("available")}
              onClick={() => window.location = mkHref({suffix: "/credits", tenant: tenant})} size={props.size}>
        <WidgetMetric>
          {intl.num(tenant.credits.amount || 0.0)}
        </WidgetMetric>
      </Widget>
  )
}

export function TotalAvailableCreditsWidget(props) {
  const {api} = useSolv()
  const {intl} = useIntlEx()
  const {data, isLoading} = useQuery({
    queryFn: async () => {
      const res = await api.getTotalAvailableCredits({tenantId: props.tenant.tenantId})
      return res?.data
    },
    queryKey: ["totalAvailableCredits"],
    enabled: !!props.tenant
  })
  return (
    <Widget title={intl.msg("credits")} subtitle={intl.msg("unused")}>
      <WidgetMetric>
        {!isLoading && data ? intl.num(data.usdValue) : "..."}
        <small className="pl-1">USD</small>
      </WidgetMetric>
    </Widget>
  )
}

export function canExtendCredits(tenant, user) {
  return (
    tenant && CreditsExpiring(tenant.credits) && (user.isSystem() || tenant.accessingAs("ADMIN/MANAGER/*") || (tenant.accessingAs("SUPERADMIN/MEMBER/*") && !tenant.credits.extendedOn))
  )
}

export function useCreditTransactionStatusOpts() {
  const {intl} = useIntlEx()
  const creditTransactionStatusOpts = [
    {
      value: "ACTIVE",
      label: intl.msg("credit_transaction_status_active"),
    },
    {
      value: "EXPIRED",
      label: intl.msg("credit_transaction_status_expired"),
    },
    {
      value: "PENDING",
      label: intl.msg("credit_transaction_status_pending"),
    },
    {
      value: "REVERSED",
      label: intl.msg("credit_transaction_status_reversed"),
    },
  ]

  function findCreditTransactionStatusOpt(status) {
    const st = status?.toUpperCase()
    if (!st) {
      return null
    }
    return creditTransactionStatusOpts.find(o => o.value === st)
  }

  return ({
    creditTransactionStatusOpts,
    findCreditTransactionStatusOpt,
  })
}

export function CreditTransactionStatus(props) {
  const {intl} = useIntlEx()
  const {findCreditTransactionStatusOpt} = useCreditTransactionStatusOpts()
  const userId = props.userId || props.row?.userId
  const statusOpt = findCreditTransactionStatusOpt(props.status || props.row?.status)
  const userLevel = props.userLevel || props.row?.userLevel
  const size = props.size || "sm"
  return (
    <>
      {
        statusOpt &&
        <TagBox>
          <TagLabel label={statusOpt.label} className={`credit-transaction-status-color-${statusOpt.value.toLowerCase()} tag-label-${size}`} tooltip={""}/>
        </TagBox>
      }
    </>
  )
}

export function useCreditTransactionTypeOpts(p) {
  const {intl} = useIntlEx()
  let creditTransactionTypeOpts = [
    {
      value: "BONUS",
      text: intl.msg("credit_transaction_type_bonus"),
      description: intl.msg("credit_transaction_type_bonus_description"),
    },
    {
      value: "CORRECTION",
      text: intl.msg("credit_transaction_type_correction"),
      description: intl.msg("credit_transaction_type_correction_description"),
    },
    {
      value: "DEPOSIT",
      text: intl.msg("credit_transaction_type_deposit"),
      description: intl.msg("credit_transaction_type_deposit_description"),
    },
    {
      value: "TRANSFER",
      text: intl.msg("credit_transaction_type_transfer"),
      description: intl.msg("credit_transaction_type_transfer_description"),
    },    {
      value: "EXPIRE",
      text: intl.msg("credit_transaction_type_expire"),
      description: intl.msg("credit_transaction_type_expire_description"),
    },
    {
      value: "PENALTY",
      text: intl.msg("credit_transaction_type_penalty"),
      description: intl.msg("credit_transaction_type_penalty_description"),
    },
    {
      value: "PURCHASE",
      text: intl.msg("credit_transaction_type_purchase"),
      description: intl.msg("credit_transaction_type_purchase_description"),
    },
    {
      value: "PURCHASE_PENDING",
      text: intl.msg("credit_transaction_type_purchase_pending"),
      description: intl.msg("credit_transaction_type_purchase_pending_description"),
    },
    {
      value: "PUBLISH",
      text: intl.msg("credit_transaction_type_publish"),
      description: intl.msg("credit_transaction_type_publish_description"),
    },
    {
      value: "REFUND",
      text: intl.msg("credit_transaction_type_refund"),
      description: intl.msg("credit_transaction_type_refund_description"),
    },
    {
      value: "RETURN",
      text: intl.msg("credit_transaction_type_return"),
      description: intl.msg("credit_transaction_type_return_description"),
    },
    {
      value: "REVIVE",
      text: intl.msg("credit_transaction_type_revive"),
      description: intl.msg("credit_transaction_type_revive_description"),
    },
    {
      value: "REVERSE",
      text: intl.msg("credit_transaction_type_reverse"),
      description: intl.msg("credit_transaction_type_reverse_description"),
    },
    {
      value: "REWARD",
      text: intl.msg("credit_transaction_type_reward"),
      description: intl.msg("credit_transaction_type_reward_description"),
    },
    {
      value: "SERVICE",
      text: intl.msg("credit_transaction_type_service"),
      description: intl.msg("credit_transaction_type_service_description"),
    },
    {
      value: "TEST",
      text: intl.msg("credit_transaction_type_test"),
      description: intl.msg("credit_transaction_type_test_description"),
    },
    {
      value: "TRANSFER",
      text: intl.msg("credit_transaction_type_transfer"),
      description: intl.msg("credit_transaction_type_transfer_description"),
    },
  ]

  if (Array.isArray(p)) {
    creditTransactionTypeOpts = creditTransactionTypeOpts.filter((c) => p.includes(c.value))
  }
  else if ("function" === typeof(p)) {
    creditTransactionTypeOpts = creditTransactionTypeOpts.map(p).filter(Boolean)
  }

  creditTransactionTypeOpts = creditTransactionTypeOpts.sort((a, b) => a.text.localeCompare(b.text))

  creditTransactionTypeOpts.forEach((c) => {
    if (!c.label) {
      c.label = (
        <>
          <div>{c.text}</div>
          <small>{c.description}</small>
        </>
      );
    }
  });

  function findCreditTransactionTypeOpt(status) {
    const st = status?.toUpperCase()
    if (!st) {
      return null
    }
    return creditTransactionTypeOpts.find(o => o.value === st)
  }

  return ({
    creditTransactionTypeOpts,
    findCreditTransactionTypeOpt,
  })
}

export function usePaymentMethodOpts(p) {
  const {intl} = useIntlEx()
  let paymentMethodOpts = [
    {
      value: "BANK",
      label: intl.msg("payment_method_bank"),
    },
    {
      value: "CASH",
      label: intl.msg("payment_method_cash"),
    },
    {
      value: "CREDITS",
      label: intl.msg("payment_method_credits"),
    },
    {
      value: "CRYPTO",
      label: intl.msg("payment_method_crypto"),
    },
    {
      value: "OTHER",
      label: intl.msg("payment_method_other"),
    },
    {
      value: "PAYPAL",
      label: intl.msg("payment_method_paypal"),
    },
    {
      value: "PAYPAL_CHECKOUT",
      label: intl.msg("payment_method_paypal_checkout"),
    },
    {
      value: "STRIPE",
      label: intl.msg("payment_method_stripe"),
    },
    {
      value: "TEST",
      label: intl.msg("payment_method_test"),
    },
    {
      value: "WISE",
      label: intl.msg("payment_method_wise"),
    },
  ]

  if (Array.isArray(p)) {
    paymentMethodOpts = paymentMethodOpts.filter((c) => p.includes(c.value))
  }
  else if ("function" === typeof(p)) {
    paymentMethodOpts = paymentMethodOpts.map(p).filter(Boolean)
  }

  function findPaymentMethodOpt(v) {
    const st = v?.toUpperCase()
    if (!st) {
      return null
    }
    return paymentMethodOpts.find(o => o.value === st)
  }

  function PaymentMethod(props) {
    const paymentMethod = props.paymentMethod || props.paymentMethodId || props.row?.paymentMethod || props.row?.paymentMethodId
    const opt = findPaymentMethodOpt(paymentMethod)
    return (
      opt && opt.label &&
        <span>{opt.label}</span>
    )
  }

  return ({
    paymentMethodOpts,
    findPaymentMethodOpt,
    PaymentMethod,
  })
}

export function PurchaseCreditsControls(props) {

  const {env, api, session, setBusy} = useSolv()
  const {intl} = useIntlEx()

  const [credits, setCredits] = useState(null)
  const [touched, setTouched] = useState(null)

  const [paymentTransactionId, setPaymentTransactionId, paymentTransactionIdRef] = useStateRef(null)
  const [paymentMethodAccordianState, setPaymentMethodAccordianState] = useState("BOTH")
  const [invoiceNo, setInvoiceNo, invoiceNoRef] = useStateRef(null)
  const [purchaseOrderNo, setPurchaseOrderNo] = useState(null)
  const [billingInfo, updateBillingInfo] = useReducer(pathReducer, props?.tenant?.billingInfo ?  props.tenant.billingInfo : props?.tenant ? {name: props.tenant.displayName, address: props.tenant.contactInfo} : {})
  const [bankAccounts, setBankAccounts] = useState(null)
  const [pendingTransaction, setPendingTransaction, pendingTransactionRef] = useStateRef(null)

  const invoiceRef = useRef(null)
  const handlePrint = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: invoiceNoRef.current,
    pageStyle: `@page { @top-left { content: '  '; } @bottom-left { content: '${window.location.href}'} @bottom-right { content: counter(page) '/' counter(pages); }`,
    onAfterPrint: handleAfterPrint,
  });


  useEffect(() => {
    if (props?.paymentInfo.minimumCredits > 0) {
      // log.debug(">>>Z: PurchaseCreditsControls: useEffect: paymentInfo=", props.paymentInfo)
      setCredits(props?.paymentInfo.minimumCredits)
    }
  }, [props?.paymentInfo])

  useEffect(() => {
    if (props?.tenant) {
      // log.debug(">>>Z: tenant=", props.tenant)
      updateBillingInfo({path: ".", value: props?.tenant?.billingInfo ?  props.tenant.billingInfo : props?.tenant ? {name: props.tenant.displayName, address: props.tenant.contactInfo} : {}})
      api.getBankAccountsByCurrencyCode(props.tenant.currencyCode)
        .then(({data}) => {
          setBankAccounts(data)
        })
    }
  }, [props?.tenant])

  function handlePurchaseOrderNoChange(e) {
    setPurchaseOrderNo(e.target.value)
  }

  function handleBillingNameChange(e) {
    updateBillingInfo({path: "name", value: e.target.value})
  }

  function handleBillingAddressChange(e) {
    updateBillingInfo({path: "address", value: e.target.value})
  }

  function handleCreditsChange(v) {
    setCredits(v)
    setTouched(true)
  }

  async function handlePaypalCreateOrder(orderData, actions) {

    setBusy(intl.msg("working"))

    try {

      const currencyCode = props.paymentInfo.currencyCode ? props.paymentInfo.currencyCode : "USD"

      const {data} = await api.createPaymentTransaction({
        tenantId: props.tenant.tenantId,
        paymentTransactionType: "PURCHASE",
        paymentMethodId: "PAYPAL_CHECKOUT",
        amount: credits,
        currencyCode: currencyCode,
        usdMultiplier: props.paymentInfo.usdMultiplier,
        details: orderData
      })

      if (data) {

        setPaymentTransactionId(data.paymentTransactionId)
        setInvoiceNo(data.invoiceNo)

        try {
          const res = await actions.order.create({
            purchase_units: [
              {
                custom_id: data.paymentTransactionId,
                invoice_id: data.invoiceNo,
                amount: {
                  currency_code: currencyCode,
                  value: credits
                }
              }
            ],
            application_context: {
              brand_name: env.brandName,
              shipping_preference: "NO_SHIPPING"
            }
          })
          return res
        }
        catch (error) {
          log.error("handlePaypalCreateOrder: Error: 1:", error)
          Promise.resolve(props.onFailure?.({type: "error", text: intl.msg("error_paypal_failed")}))
          return false
        }

      }
      else {
        log.error("handlePaypalCreateOrder: Error: 2:")
        // log.debug("handlePaypalCreateOrder: error=", data.error)
        Promise.resolve(props.onFailure?.({type: "error", text: intl.msg("error_paypal_failed")}))
        return false
      }
    }
    catch (error) {
      log.error("handlePaypalCreateOrder: Error: 3:", error)
      Promise.resolve(props.onFailure?.({type: "error", text: intl.msg("error_paypal_failed")}))
      return false
    }
    finally {
      setBusy(null)
    }
  }

  async function handlePaypalApprove(orderData, actions) {

    setBusy(intl.msg("working"))

    let details

    try {
      details = await actions.order.capture() || {}
    }
    catch (error) {
      log.error("handlePurchaseWithPaypal: Error: 1:", error)
      Promise.resolve(props.onFailure?.({type: "error", text: intl.msg("error_paypal_failed")}))
      return
    }

    try {

      if (props.paymentInfo.details) {
        details = {...details, ...props.paymentInfo.details}
      }

      const payload = {
        paymentTransactionId: paymentTransactionIdRef.current,
        purchaseType: "CREDITS",
        description: props.paymentInfo.description,
        details: details,
      }

      // log.debug(`handlePaypalApprove: payload=`, payload)

      const res = await api.purchaseCredits(props.tenant.tenantId, payload)

      Promise.resolve(props.onSuccess?.(res))

    }
    catch (error) {
      log.error("handlePurchaseWithPaypal: Error: 1:", error)
      Promise.resolve(props.onFailure?.({type: "error", text: intl.msg("error_paypal_failed")}))
    }
    finally {
      setBusy(null)
    }

  }

  async function handleStripeCreateOrder() {
    setBusy(intl.msg("working"));

    const currencyCode = props.paymentInfo.currencyCode ? props.paymentInfo.currencyCode : "USD"

    try {
      const { data } = await api.createPaymentTransaction({
        tenantId: props.tenant.tenantId,
        paymentTransactionType: "PURCHASE",
        paymentMethodId: "STRIPE",
        amount: credits,
        currencyCode: currencyCode,
        usdMultiplier: props.paymentInfo.usdMultiplier,
        details: {}
      });

      log.debug(">>>Z: handleStripeCreateOrder: resp=", data)

      if (data && data.paymentTransactionId && data.details?.stripeClientSecret) {
        setPaymentTransactionId(data.paymentTransactionId);
        return data.details.stripeClientSecret; // The StripeButton needs this!
      }
      else {
        // eslint-disable-next-line no-unused-expressions
        props.onFailure?.({type: "error", text: "Failed to initialize Stripe."});
        return false;
      }
    }
    catch (error) {
      // eslint-disable-next-line no-unused-expressions
      props.onFailure?.({type: "error", text: "Failed to initialize Stripe."});
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function handleStripeApprove(paymentIntent) {
    log.debug(">>>Z: handleStripeApprove: paymentIntent=", paymentIntent)
    setBusy(intl.msg("working"));
    try {
      const payload = {
        paymentTransactionId: paymentTransactionIdRef.current,
        purchaseType: "CREDITS",
        description: props.paymentInfo.description,
        details: {
          stripePaymentIntentId: paymentIntent.id
        },
      }

      // Finalize the purchase on your backend
      const res = await api.purchaseCredits(props.tenant.tenantId, payload);
      // eslint-disable-next-line no-unused-expressions
      props.onSuccess?.(res);
    }
    catch (error) {
      // eslint-disable-next-line no-unused-expressions
      props.onFailure?.({type: "error", text: "Payment succeeded but failed to register credits."});
    }
    finally {
      setBusy(null);
    }
  }

  function handleOnlinePaymentClick(e) {
    setPaymentMethodAccordianState("ONLINE")
  }

  function handleOnlinePaymentCancel(e) {
    setPaymentMethodAccordianState("BOTH")
  }

  function handleOfflinePaymentCancel(e) {
    setPaymentMethodAccordianState("BOTH")
  }

  function handleOfflinePaymentClick(e) {
    setPaymentMethodAccordianState("OFFLINE")
  }

  function isSandbox() {
    return ("DEV" === env.STAGE) || ["DEMO", "TEST"].includes(props.tenant?.tenantPlanId)
  }

  function handleAfterPrint() {
    window.location = mkHref({suffix: "/credits", tenant: props.tenant, user: session.user})
  }

  async function handleOfflinePaymentRegisterClick() {

    const payload = {
      paymentMethodId: "BANK",
      credits: credits,
      description: intl.msg("credit_transaction_description_register_purchase_credits"),
      purchaseOrderNo: purchaseOrderNo,
      billingInfo: billingInfo,
    }

    setBusy(intl.msg("working"))

    try {
      const {data} = await api.registerCreditTransaction(props.tenant.tenantId, payload)
      setPendingTransaction(data)
      handlePrint()
    }
    catch (error) {
      Promise.resolve(props.onFailure?.({type: "error", text: intl.msg("error_failed")}))
    }
    finally {
      setBusy(null)
    }
  }

  return (
    props?.tenant ? (
      <>
        <FormGroup>
          <FormGroup.Label htmlFor="inp_credits" text={intl.msg("credits_add_credits")} description={intl.msg("credits_add_credits_description")}/>
          <FormGroup.Controls>
            <div className="form-control-wrapper">
              <div style={{width: "120px"}}>
                <NumericInput id="inp_credits" key={`inp_credits_`} className="form-control"  min={props?.paymentInfo.minimumCredits} max={9999999.99} decimals={2} value={credits} onChange={handleCreditsChange}/>
              </div>
              <div className="form-control-suffix pl-2 pr-2">Credits</div>
            </div>
            <InfoBlock>
              <InfoText validate="min-value">{intl.msg("credits_purchase_helptext_min_value", {credits: `${intl.num(props?.paymentInfo.minimumCredits)} Credits`})}</InfoText>
              <InfoText>{intl.msg("credits_purchase_helptext_conversion_rate", {credits: intl.num(credits), currencyCode: (props?.paymentInfo.currencyCode || "USD")})}</InfoText>
              <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
            </InfoBlock>
          </FormGroup.Controls>
        </FormGroup>

        {
          credits && credits >= props?.paymentInfo.minimumCredits &&
            <FormGroup>
              <FormGroup.Label htmlFor="inp_paymentMethod" text={intl.msg("credits_payment_method")} description={intl.msg("credits_add_payment_method_description")}/>
              <FormGroup.Controls>
                <div className={styles["payment-buttons-container"]}>

                  {
                    ["BOTH", "ONLINE"].includes(paymentMethodAccordianState) ? (
                      <>
                        {
                          <div className={styles["online-payment-buttons-container"]}>

                            {
                              "PAYPAL" === props.tenant?.effProps?.paymentProvider ? (
                                <PayPalButton
                                  options={{
                                    clientId: (isSandbox() ? env.paypalTestClientId : env.paypalLiveClientId),
                                    currency: `${props.tenant?.currencyCode ? props.tenant.currencyCode : "USD"}`,
                                  }}
                                  createOrder={handlePaypalCreateOrder}
                                  onApprove={handlePaypalApprove}
                                  onClick={handleOnlinePaymentClick}
                                  onCancel={handleOnlinePaymentCancel}
                                />
                              ) : "STRIPE" === props.tenant?.effProps?.paymentProvider ? (
                                <StripeControls
                                  options={{
                                    clientId: env.stripePublishableKey
                                  }}
                                  createOrder={handleStripeCreateOrder}
                                  onApprove={handleStripeApprove}
                                  onClick={() => setPaymentMethodAccordianState("ONLINE")}
                                  onCancel={() => setPaymentMethodAccordianState("BOTH")}

                                />
                              ) : (
                                <></>
                              )
                            }
                          </div>
                        }
                      </>
                    ) : (
                      <></>
                    )
                  }

                  {
                    ["BOTH"].includes(paymentMethodAccordianState) ? (
                      props?.options?.offlinePayment ? (
                        <div className={styles["offline-payment-buttons-container"]}>
                          <button type="button" className="btn btn-black btn-payment" onClick={handleOfflinePaymentClick}>
                            <Icon name="offline_payment" width="2em" height="2em"/>
                            {intl.msg("credits_offline_payment")}
                          </button>
                        </div>
                      ) : (
                        <></>
                      )
                    ) : (
                      <></>
                    )
                  }

                  {
                    ["OFFLINE"].includes(paymentMethodAccordianState) ? (
                      props?.options?.offlinePayment ? (
                        <>

                          <div className={styles["offline-payment-buttons-header"]}>
                            <div>
                              {intl.msg("credits_offline_payment")}
                            </div>
                            <div className="d-flex gap-1">
                              <ActionButton text={intl.msg("register")} icon="ok" flavor="primary" disabled={!billingInfo?.name || !billingInfo?.address} onClick={handleOfflinePaymentRegisterClick}/>
                              <ActionButton icon="cross" flavor="link" onClick={handleOfflinePaymentCancel}/>
                            </div>
                          </div>

                          <FormGroups>

                            <FormGroup>
                              <FormGroup.Text>
                                {intl.msg("credits_bank_transfer_instructions", {contact_for_other_methods: (chunks) => <a className="text-link" href="mailto:support@solv.technology?subject=Offline+Payment">{chunks}</a>})}
                              </FormGroup.Text>
                            </FormGroup>

                            <FormGroup>
                              <FormGroup.Label htmlFor="inp_billingName" text={intl.msg("credit_transaction_billing_name")} description={intl.msg("credit_transaction_billing_name_description")}/>
                              <FormGroup.Controls>
                                <input id="inp_billingName" type="text" className="form-control" value={billingInfo?.name} onChange={handleBillingNameChange}/>
                                <InfoBlock>
                                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                                </InfoBlock>
                              </FormGroup.Controls>
                            </FormGroup>

                            <FormGroup>
                              <FormGroup.Label htmlFor="inp_billingAddress" text={intl.msg("credit_transaction_billing_address")} description={intl.msg("credit_transaction_billing_address_description")}/>
                              <FormGroup.Controls>
                                <textarea id="inp_billingAddress" className="form-control" value={billingInfo?.address} onChange={handleBillingAddressChange}/>
                                <InfoBlock>
                                  <InfoText validate="required">{intl.msg("helptext_required")}</InfoText>
                                </InfoBlock>
                              </FormGroup.Controls>
                            </FormGroup>

                            <FormGroup>
                              <FormGroup.Label text={intl.msg("credit_transaction_purchase_order_no")} description={intl.msg("credit_transaction_purchase_order_no_description")}/>
                              <FormGroup.Controls>
                                <input id="inp_bankAccountName" type="text" className="form-control" value={purchaseOrderNo} onChange={handlePurchaseOrderNoChange}/>
                                <InfoBlock>
                                  <InfoText validate="optional">{intl.msg("helptext_optional")}</InfoText>
                                </InfoBlock>
                              </FormGroup.Controls>
                            </FormGroup>

                          </FormGroups>
                        </>
                      ) : (
                        <></>
                      )
                    ) : (
                      <></>
                    )
                  }

                </div>
                <InfoBlock className="pt-1 pb-2">
                  <InfoText>{intl.msg("payment_agree_tos")}</InfoText>
                </InfoBlock>
              </FormGroup.Controls>
            </FormGroup>
        }

        <CreditTransactionBill
          ref={invoiceRef}
          tenant={props.tenant}
          creditTransaction={pendingTransaction}
          billingInfo={billingInfo}
          bankAccounts={bankAccounts}
        />

      </>
    ) : (
      <></>
    )
  )

}

